import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface AnalyticsSummary {
  readonly totalCalls: number;
  readonly totalSuccess: number;
  readonly totalFailure: number;
  readonly successRate: number;
  readonly avgResponseMs: number;
  readonly p95ResponseMs: number;
  readonly apiCount: number;
}
interface TopApiRow {
  readonly apiId: string;
  readonly apiCode: string | null;
  readonly apiName: string | null;
  readonly totalCalls: number;
  readonly totalSuccess: number;
  readonly totalFailure: number;
}

/** M29 — API Analytics Dashboard (§25 / §33). Tenant-wide gateway usage KPIs
 * (call volume, success rate, latency percentiles) plus the top APIs by
 * traffic. */
export function M29ApiAnalyticsPage(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t: tr } = useTranslation();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [top, setTop] = useState<TopApiRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: AnalyticsSummary }>('/v1/eip/analytics/summary', { token }),
      apiRequest<{ data: TopApiRow[] }>('/v1/eip/analytics/top', { token }),
    ])
      .then(([s, t]) => { if (alive) { setSummary(s.data); setTop(t.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="api-analytics">
      <h1>{tr('m29.apiAnalytics.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !summary ? <p>{tr('m29.apiAnalytics.loading')}</p> : (
        <>
          <section aria-label="kpi-cards" className="card-grid">
            <article><h2>{tr('m29.apiAnalytics.cardTotalCalls')}</h2><p aria-label="kpi-total-calls">{summary.totalCalls}</p></article>
            <article><h2>{tr('m29.apiAnalytics.cardSuccessRate')}</h2><p aria-label="kpi-success-rate">{number(summary.successRate)}%</p></article>
            <article><h2>{tr('m29.apiAnalytics.cardAvgResponse')}</h2><p aria-label="kpi-avg-ms">{summary.avgResponseMs} ms</p></article>
            <article><h2>{tr('m29.apiAnalytics.cardP95Response')}</h2><p aria-label="kpi-p95-ms">{summary.p95ResponseMs} ms</p></article>
            <article><h2>{tr('m29.apiAnalytics.cardApisTracked')}</h2><p aria-label="kpi-api-count">{summary.apiCount}</p></article>
          </section>
          <section aria-label="top-apis">
            <h2>{tr('m29.apiAnalytics.topHeading')}</h2>
            <p aria-label="top-count">{tr('m29.apiAnalytics.apiCount', { count: top.length })}</p>
            {top.length > 0 && (
              <table aria-label="top-table">
                <thead><tr><th>{tr('m29.apiAnalytics.colRank')}</th><th>{tr('m29.apiAnalytics.colApi')}</th><th>{tr('m29.apiAnalytics.colCalls')}</th><th>{tr('m29.apiAnalytics.colSuccess')}</th><th>{tr('m29.apiAnalytics.colFailures')}</th></tr></thead>
                <tbody>
                  {top.map((r, i) => (
                    <tr key={r.apiId}>
                      <td>{i + 1}</td><td>{r.apiCode ? (r.apiName ? `${r.apiCode} — ${r.apiName}` : r.apiCode) : '—'}</td><td>{r.totalCalls}</td>
                      <td>{r.totalSuccess}</td><td>{r.totalFailure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
